import {
  fetchGetContext,
  fetchGetContextByStateKey,
  fetchGetProfile,
  fetchGetStateByStateKey,
} from '@iwoca/lapi-client/edge';
import {
  fetchGetSellerBranding,
  fetchGetSharedFunctionality,
} from '@iwoca/lapi-client/iwocapay';
import { useSuspenseQuery } from '@tanstack/react-query';

import { AuthContextType } from './lapiHooks';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useSuspenseStateKey } from '../../hooks/useStateKey.hook';

export function useSuspenseGetProfile() {
  const { data, refetch } = useSuspenseQuery({
    queryKey: ['fetchGetProfile'],
    queryFn: () => fetchGetProfile({}),
  });

  return {
    profile: data.data,
    getProfile: refetch,
  };
}

export function useSuspenseGetContext() {
  const impersonationStateKey = useQueryParam('state_key');

  const getContextFunction = impersonationStateKey
    ? () => fetchGetContextByStateKey({ stateKey: impersonationStateKey })
    : () => fetchGetContext({});

  const { data, isError, refetch } = useSuspenseQuery({
    queryKey: ['fetchGetContext'],
    queryFn: async () => {
      const data = await getContextFunction();
      return data;
    },
  });

  const contextData: AuthContextType = {
    stateKey: data?.data?.state_key as AuthContextType['stateKey'],
    // @ts-expect-error LAPI not in sync
    isSeller: data?.data?.is_iwocapay_seller,
    isLoggedIn: !!data && !isError,
    user_signup_finished: data?.data?.user_signup_finished || null,
    isOnSoleTraderPilot: data?.data?.is_iwocapay_st_pilot || false,
    // @ts-expect-error LAPI not in sync
    groups: data?.data.groups || null,
  };

  return {
    context: { ...data?.data, ...contextData },
    getContext: refetch,
  };
}

export function useSuspenseUserGroups() {
  const { context, getContext } = useSuspenseGetContext();

  return {
    groups: context.groups || [],
    isOwner: context?.groups?.includes('customers') || false,
    isStaff: context?.groups?.includes('team') || false,
    isAdmin: context?.groups?.includes('iwocapay_seller_admin'),
    isEditor: context?.groups?.includes('iwocapay_seller_editor'),
    isContributor: context?.groups?.includes('iwocapay_seller_contributor'),
    isViewer: context?.groups?.includes('iwocapay_seller_viewer'),
    fetchSiteContext: getContext,
  };
}

export function useSuspenseGetStateByStateKey() {
  const { stateKey } = useSuspenseStateKey();

  const { data, refetch } = useSuspenseQuery({
    queryKey: ['fetchGetState'],
    queryFn: () => fetchGetStateByStateKey({ stateKey: stateKey! }),
  });

  return {
    state: data?.data,
    refetchState: refetch,
  };
}

export function useSuspenseGetSellerBranding() {
  const { stateKey } = useSuspenseStateKey();

  const { data, refetch } = useSuspenseQuery({
    queryKey: ['fetchGetSellerBranding'],
    queryFn: () => fetchGetSellerBranding({ stateKey: stateKey! }),
  });

  return {
    sellerBranding: data.data,
    refetch,
  };
}

export function useSuspenseGetSharedFunctionality() {
  const { stateKey } = useSuspenseStateKey();
  if (!stateKey) throw Error('User has no state key');

  const { data, refetch } = useSuspenseQuery({
    queryKey: ['fetchGetSharedFunctionality'],
    queryFn: () =>
      fetchGetSharedFunctionality({
        stateKey: stateKey,
      }),
  });

  return {
    functionality: data.data,
    refetch,
  };
}
